<template>
    <div 
        class="pivot-cell" 
        :class="cellTypeClass"
        :style="cellStyle"
        v-tippy="tooltipConfig"
    >
        <span class="cell-value">{{ formattedValue }}</span>
    </div>
</template>

<script>
import Vue from "vue";
import PivotColorEngine from "../utils/PivotColorEngine";

export default Vue.extend({
    name: "PivotCell",
    props: {
        value: {
            type: [Number, String],
            default: null
        },
        cellType: {
            type: String,
            default: 'data', // 'data', 'header', 'total', 'subtotal'
            validator: (value) => ['data', 'header', 'total', 'subtotal'].includes(value)
        },
        colorConfig: {
            type: Object,
            default: null
        },
        min: {
            type: Number,
            default: 0
        },
        max: {
            type: Number,
            default: 0
        },
        decimalFormat: {
            type: Number,
            default: 2
        },
        emptyCell: {
            type: String,
            default: '-'
        },
        tooltipContent: {
            type: String,
            default: null
        },
        unit: {
            type: Object,
            default: null // { value: '₺', position: 'left'|'right' }
        }
    },
    computed: {
        cellTypeClass() {
            return {
                'is-data': this.cellType === 'data',
                'is-header': this.cellType === 'header',
                'is-total': this.cellType === 'total',
                'is-subtotal': this.cellType === 'subtotal'
            };
        },
        formattedValue() {
            if (this.value === null || this.value === undefined || this.value === '') {
                return this.emptyCell;
            }

            if (this.cellType === 'header') {
                return this.value;
            }

            const numValue = parseFloat(this.value);
            if (isNaN(numValue)) {
                return this.value;
            }

            const formatted = numValue.toLocaleString('tr-TR', {
                minimumFractionDigits: this.decimalFormat,
                maximumFractionDigits: this.decimalFormat
            });

            // Birim varsa ekle (none değilse)
            if (this.unit && this.unit.value && this.unit.value !== 'none') {
                if (this.unit.position === 'left') {
                    return `${this.unit.value}${formatted}`;
                } else {
                    return `${formatted}${this.unit.value}`;
                }
            }

            return formatted;
        },
        backgroundColor() {
            if (this.cellType !== 'data') return null;
            if (!this.colorConfig) return null;

            // Eğer heatmap açıksa ve ignoreZero true ise, 0 veya null değerleri renklendirme
            if (this.colorConfig.heatMap && this.colorConfig.heatMap.enabled) {
                const ignoreZero = this.colorConfig.heatMap.ignoreZero !== false; // default true
                if (ignoreZero && (this.value === 0 || this.value === null || this.value === undefined)) {
                    return null;
                }
            }

            return PivotColorEngine.getCellColor(
                this.value,
                this.colorConfig,
                this.min,
                this.max
            );
        },
        textColor() {
            if (!this.backgroundColor) return null;
            return PivotColorEngine.getTextColor(this.backgroundColor);
        },
        cellStyle() {
            const style = {};
            
            if (this.backgroundColor) {
                style.backgroundColor = this.backgroundColor;
            }
            
            if (this.textColor) {
                style.color = this.textColor;
            }

            return style;
        },
        tooltipConfig() {
            if (!this.tooltipContent && this.cellType !== 'data') {
                return null;
            }

            return {
                placement: 'top',
                theme: 'light',
                arrow: true,
                animation: 'scale-extreme',
                duration: [200, 200],
                content: this.tooltipContent || this.formattedValue
            };
        }
    }
});
</script>

<style scoped lang="scss">
.pivot-cell {
    padding: 8px 12px;
    border: 1px solid #e8e8e8;
    text-align: right;
    font-size: 13px;
    transition: background-color 0.2s, color 0.2s;

    &.is-header {
        background-color: #fafafa;
        font-weight: 600;
        text-align: left;
        position: sticky;
        z-index: 2;
    }

    &.is-total {
        background-color: #f0f0f0;
        font-weight: 700;
        border-top: 2px solid #d9d9d9;
    }

    &.is-subtotal {
        background-color: #f5f5f5;
        font-weight: 600;
        font-style: italic;
    }

    &.is-data {
        &:hover {
            box-shadow: inset 0 0 0 1px #1890ff;
        }
    }

    .cell-value {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}
</style>
